<template>
  <main class="profile-page" :class="{ 'dark-mode': isDarkMode }">
    <section v-if="!isAuthenticated" class="page-shell auth-shell" aria-labelledby="auth-title">
      <div class="md-card auth-card">
        <header class="section-header centered">
          <p class="section-kicker">{{ mode === 'login' ? 'Profil' : 'Yangi akkaunt' }}</p>
          <h1 id="auth-title">{{ mode === 'login' ? 'Akkauntga kirish' : 'Ro‘yxatdan o‘tish' }}</h1>
          <p>{{ mode === 'login' ? 'So‘zlaringiz va profilingizni boshqarish uchun kiring.' : 'Yangi so‘zlar qo‘shish uchun akkaunt yarating.' }}</p>
        </header>

        <form class="md-form" @submit.prevent="mode === 'login' ? login() : register()" novalidate>
          <div class="field">
            <label for="email">Email</label>
            <input
              id="email"
              v-model.trim="form.email"
              class="md-input"
              :class="{ 'is-error': Boolean(errorMessage) }"
              type="email"
              autocomplete="email"
              required
              placeholder="email@example.com"
              :aria-invalid="Boolean(errorMessage)"
            />
          </div>

          <div v-if="mode === 'register'" class="field">
            <label for="username">Username</label>
            <input
              id="username"
              v-model.trim="form.username"
              class="md-input"
              :class="{ 'is-error': Boolean(usernameError) }"
              autocomplete="username"
              required
              placeholder="username"
              :aria-invalid="Boolean(usernameError)"
              aria-describedby="username-error"
            />
            <p v-if="usernameError" id="username-error" class="supporting-text error" role="alert">{{ usernameError }}</p>
          </div>

          <div v-if="mode === 'register'" class="field">
            <label for="name">Ismingiz</label>
            <input
              id="name"
              v-model.trim="form.name"
              class="md-input"
              :class="{ 'is-error': hasNameError }"
              autocomplete="name"
              required
              placeholder="To‘liq ismingiz"
              :aria-invalid="hasNameError"
              aria-describedby="name-error"
            />
            <p v-if="hasNameError" id="name-error" class="supporting-text error" role="alert">
              Ism kamida 2ta belgi bo‘lishi kerak.
            </p>
          </div>

          <div class="field">
            <label for="password">Parol</label>
            <input
              id="password"
              v-model="form.password"
              class="md-input"
              :class="{ 'is-error': Boolean(errorMessage) }"
              type="password"
              :autocomplete="mode === 'login' ? 'current-password' : 'new-password'"
              required
              placeholder="Parol"
              :aria-invalid="Boolean(errorMessage)"
            />
          </div>

          <div v-if="mode === 'register'" class="field">
            <label for="confirmPassword">Parolni tasdiqlash</label>
            <input
              id="confirmPassword"
              v-model="form.confirmPassword"
              class="md-input"
              :class="{ 'is-error': hasPasswordMismatch }"
              type="password"
              autocomplete="new-password"
              required
              placeholder="Parolni qayta kiriting"
              :aria-invalid="hasPasswordMismatch"
              aria-describedby="confirm-password-error"
            />
            <p v-if="hasPasswordMismatch" id="confirm-password-error" class="supporting-text error" role="alert">
              Parollar mos kelmayapti.
            </p>
          </div>

          <p v-if="errorMessage" class="status-message error" role="alert">{{ errorMessage }}</p>

          <button type="submit" class="md-button filled" :disabled="loading || hasNameError || hasPasswordMismatch">
            <span v-if="loading" class="button-spinner" aria-hidden="true"></span>
            {{ mode === 'login' ? 'Kirish' : 'Ro‘yxatdan o‘tish' }}
          </button>

          <p class="toggle-text">
            {{ mode === 'login' ? "Akkauntingiz yo‘qmi?" : 'Akkauntingiz bormi?' }}
            <button type="button" class="text-button" @click="toggleMode">
              {{ mode === 'login' ? 'Ro‘yxatdan o‘tish' : 'Kirish' }}
            </button>
          </p>
        </form>
      </div>
    </section>

    <section v-else class="page-shell" aria-labelledby="profile-title">
      <div v-if="loading" class="md-card loading-card" role="status" aria-live="polite">
        <div class="linear-progress" aria-hidden="true"></div>
        <p>Profilingiz yuklanmoqda...</p>
      </div>

      <template v-else>
        <header v-if="profile" class="md-card profile-card">
          <div class="profile-identity">
            <div class="avatar" :style="profilePictureStyle">
              <img v-if="profilePicture" :src="profilePicture" alt="Profil rasmi" />
              <span v-else aria-hidden="true">{{ firstLetter }}</span>
            </div>
            <div>
              <p class="section-kicker">Profil</p>
              <h1 id="profile-title">{{ profile.username }}</h1>
              <p class="muted">{{ profile.email }}</p>
            </div>
          </div>
          <button type="button" class="md-button tonal danger" @click="logout">
            <i class="bi bi-box-arrow-right" aria-hidden="true"></i>
            <span>Chiqish</span>
          </button>
        </header>

        <p v-if="profileStatus.text" class="status-message" :class="profileStatus.type" role="status" aria-live="polite">
          {{ profileStatus.text }}
        </p>

        <section class="md-card words-section" aria-labelledby="words-title">
          <div class="section-row">
            <div>
              <p class="section-kicker">Lug‘at</p>
              <h2 id="words-title">Sizning so‘zlaringiz</h2>
            </div>
            <span class="count-chip" aria-label="So‘zlar soni">{{ phrases.length }} so‘z</span>
          </div>

          <div v-if="error" class="state-panel error" role="alert">
            <i class="bi bi-exclamation-triangle" aria-hidden="true"></i>
            <div>
              <h3>So‘zlarni olishda xatolik yuz berdi</h3>
              <p>Keyinroq qayta urinib ko‘ring.</p>
            </div>
          </div>

          <div v-else-if="phrases.length === 0" class="state-panel">
            <i class="bi bi-file-earmark-text" aria-hidden="true"></i>
            <div>
              <h3>Hozircha so‘zlar yo‘q</h3>
              <p>Yaratilgan so‘zlar shu yerda ko‘rinadi.</p>
            </div>
          </div>

          <ul v-else class="words-list">
            <li v-for="phrase in phrases" :key="phrase.id || phrase.slug" class="word-card">
              <div class="word-meta">
                <span>@{{ phrase.author_username }}</span>
                <span aria-hidden="true">•</span>
                <time :datetime="phrase.date">{{ formatDate(phrase.date) }}</time>
              </div>

              <div class="word-heading">
                <h3>{{ phrase.title }}</h3>
                <button
                  v-if="profile && phrase.author_username === profile.username"
                  type="button"
                  class="icon-button danger"
                  :aria-label="`${phrase.title} so‘zini o‘chirish`"
                  @click="requestDelete(phrase.slug)"
                >
                  <i class="bi bi-trash" aria-hidden="true"></i>
                </button>
              </div>

              <p class="definition">{{ phrase.definition }}</p>
              <p v-if="phrase.example" class="example">
                <span>Misol:</span> {{ phrase.example }}
              </p>

              <div v-if="pendingDeleteSlug === phrase.slug" class="confirm-panel" role="alertdialog" :aria-label="`${phrase.title} so‘zini o‘chirishni tasdiqlash`">
                <p>Bu so‘z o‘chirilsinmi?</p>
                <div class="confirm-actions">
                  <button type="button" class="md-button text" @click="pendingDeleteSlug = ''">Bekor qilish</button>
                  <button type="button" class="md-button filled danger" :disabled="actionLoading" @click="deletePhrase(phrase.slug)">
                    O‘chirish
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </section>

        <section class="md-card settings-section" aria-labelledby="settings-title">
          <div>
            <p class="section-kicker">Sozlamalar</p>
            <h2 id="settings-title">Akkaunt</h2>
          </div>
          <div class="settings-actions">
            <button type="button" class="md-button tonal" @click="openPasswordDialog">
              <i class="bi bi-key" aria-hidden="true"></i>
              <span>Parolni o‘zgartirish</span>
            </button>
            <button type="button" class="md-button tonal" @click="openAccountDialog">
              <i class="bi bi-person-gear" aria-hidden="true"></i>
              <span>Username va ismni o‘zgartirish</span>
            </button>
          </div>
        </section>
      </template>
    </section>

    <div v-if="activeDialog" class="dialog-backdrop" @click.self="closeDialog">
      <section class="md-card dialog" role="dialog" aria-modal="true" :aria-labelledby="activeDialog === 'password' ? 'password-dialog-title' : 'account-dialog-title'">
        <template v-if="activeDialog === 'password'">
          <header class="dialog-header">
            <h2 id="password-dialog-title">Parolni o‘zgartirish</h2>
            <button type="button" class="icon-button" aria-label="Yopish" @click="closeDialog">
              <i class="bi bi-x-lg" aria-hidden="true"></i>
            </button>
          </header>
          <form class="md-form" @submit.prevent="changePassword">
            <div class="field">
              <label for="old-password">Joriy parol</label>
              <input id="old-password" v-model="passwordForm.oldPassword" class="md-input" type="password" autocomplete="current-password" required />
            </div>
            <div class="field">
              <label for="new-password">Yangi parol</label>
              <input id="new-password" v-model="passwordForm.newPassword" class="md-input" type="password" autocomplete="new-password" required minlength="6" />
            </div>
            <div class="field">
              <label for="new-password-confirm">Yangi parolni tasdiqlash</label>
              <input
                id="new-password-confirm"
                v-model="passwordForm.confirmPassword"
                class="md-input"
                :class="{ 'is-error': passwordDialogError }"
                type="password"
                autocomplete="new-password"
                required
                :aria-invalid="Boolean(passwordDialogError)"
              />
            </div>
            <p v-if="passwordDialogError" class="status-message error" role="alert">{{ passwordDialogError }}</p>
            <div class="dialog-actions">
              <button type="button" class="md-button text" @click="closeDialog">Bekor qilish</button>
              <button type="submit" class="md-button filled" :disabled="actionLoading">Saqlash</button>
            </div>
          </form>
        </template>

        <template v-else>
          <header class="dialog-header">
            <h2 id="account-dialog-title">Profil ma’lumotlari</h2>
            <button type="button" class="icon-button" aria-label="Yopish" @click="closeDialog">
              <i class="bi bi-x-lg" aria-hidden="true"></i>
            </button>
          </header>
          <form class="md-form" @submit.prevent="changeAccountData">
            <div class="field">
              <label for="account-username">Username</label>
              <input id="account-username" v-model.trim="accountForm.username" class="md-input" autocomplete="username" required />
            </div>
            <div class="field">
              <label for="account-name">Ism</label>
              <input id="account-name" v-model.trim="accountForm.name" class="md-input" autocomplete="name" required />
            </div>
            <p v-if="accountDialogError" class="status-message error" role="alert">{{ accountDialogError }}</p>
            <div class="dialog-actions">
              <button type="button" class="md-button text" @click="closeDialog">Bekor qilish</button>
              <button type="submit" class="md-button filled" :disabled="actionLoading">Saqlash</button>
            </div>
          </form>
        </template>
      </section>
    </div>
  </main>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import axios from 'axios';
import dayjs from 'dayjs';

const mode = ref('login');
const isAuthenticated = ref(!!localStorage.getItem('auth_token'));
const token = ref(localStorage.getItem('auth_token') || null);
const form = ref({
  username: '',
  email: '',
  name: '',
  password: '',
  confirmPassword: '',
});
const profile = ref(null);
const phrases = ref([]);
const errorMessage = ref('');
const usernameError = ref('');
const loading = ref(true);
const error = ref(false);
const activeDialog = ref('');
const actionLoading = ref(false);
const pendingDeleteSlug = ref('');
const profileStatus = ref({ type: 'success', text: '' });
const passwordDialogError = ref('');
const accountDialogError = ref('');
const passwordForm = ref({
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
});
const accountForm = ref({
  username: '',
  name: '',
});

const store = useStore();
const router = useRouter();
const isDarkMode = computed(() => store.getters.isDarkMode);

const hasNameError = computed(() => mode.value === 'register' && form.value.name.length > 0 && form.value.name.length < 2);
const hasPasswordMismatch = computed(() => (
  mode.value === 'register'
  && form.value.confirmPassword !== ''
  && form.value.password !== form.value.confirmPassword
));
const firstLetter = computed(() => (profile.value?.username ? profile.value.username.charAt(0).toUpperCase() : ''));
const profilePicture = computed(() => profile.value?.profilePicture || profile.value?.profile_picture || profile.value?.image || '');

const profilePictureStyle = computed(() => ({
  backgroundColor: profile.value?.username ? getColorFromString(profile.value.username) : 'var(--md-surface-variant)',
  color: '#fff',
}));

onMounted(async () => {
  if (token.value) {
    isAuthenticated.value = true;
    await fetchProfileData();
    await fetchUserPhrases();
  } else {
    loading.value = false;
  }
});

function toggleMode() {
  mode.value = mode.value === 'login' ? 'register' : 'login';
  errorMessage.value = '';
  usernameError.value = '';
}

async function register() {
  errorMessage.value = '';
  usernameError.value = '';

  if (hasNameError.value) return;
  if (form.value.password !== form.value.confirmPassword) {
    errorMessage.value = 'Parollar mos kelmayapti.';
    return;
  }
  if (form.value.password.length < 6) {
    errorMessage.value = 'Parol kamida 6 belgidan iborat bo‘lishi kerak.';
    return;
  }

  loading.value = true;
  try {
    await axios.post('users/register/', {
      email: form.value.email,
      username: form.value.username,
      name: form.value.name,
      password: form.value.password,
      password2: form.value.confirmPassword,
    });

    toggleMode();
    form.value = {
      username: '',
      email: '',
      name: '',
      password: '',
      confirmPassword: '',
    };
  } catch (requestError) {
    const data = requestError.response?.data;
    if (data?.username) {
      usernameError.value = data.username.join(' ');
    } else if (data?.password) {
      errorMessage.value = data.password.join(' ');
    } else if (data?.non_field_errors) {
      errorMessage.value = data.non_field_errors.join(' ');
    } else {
      errorMessage.value = 'Ro‘yxatga olish muvaffaqiyatsiz tugadi. Ma’lumotlarni tekshiring.';
    }
  } finally {
    loading.value = false;
  }
}

async function login() {
  errorMessage.value = '';
  loading.value = true;
  try {
    const res = await axios.post('users/token/', {
      email: form.value.email,
      password: form.value.password,
    });
    token.value = res.data.access;
    localStorage.setItem('auth_token', token.value);
    isAuthenticated.value = true;
    await fetchProfileData();
    await fetchUserPhrases();
  } catch (requestError) {
    errorMessage.value = requestError.response?.data?.detail || 'Noto‘g‘ri elektron pochta yoki parol. Qayta urinib ko‘ring.';
    loading.value = false;
  }
}

async function fetchProfileData() {
  if (!token.value) return;
  try {
    const res = await axios.get('users/profile/', {
      headers: { Authorization: `Bearer ${token.value}` },
    });
    profile.value = res.data;
  } catch {
    logout();
    profileStatus.value = { type: 'error', text: 'Profil ma’lumotlarini olishda xatolik yuz berdi.' };
  }
}

async function fetchUserPhrases() {
  if (!token.value || !profile.value) {
    loading.value = false;
    return;
  }

  error.value = false;
  try {
    const response = await axios.get('phrases/user-phrases/', {
      headers: { Authorization: `Bearer ${token.value}` },
    });
    phrases.value = response.data.results || [];
  } catch {
    phrases.value = [];
    error.value = true;
  } finally {
    loading.value = false;
  }
}

function logout() {
  token.value = null;
  profile.value = null;
  phrases.value = [];
  localStorage.removeItem('auth_token');
  isAuthenticated.value = false;
  loading.value = false;
  router.push('/profile');
}

function openPasswordDialog() {
  passwordForm.value = {
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  };
  passwordDialogError.value = '';
  activeDialog.value = 'password';
}

function openAccountDialog() {
  accountForm.value = {
    username: profile.value?.username || '',
    name: profile.value?.name || '',
  };
  accountDialogError.value = '';
  activeDialog.value = 'account';
}

function closeDialog() {
  if (actionLoading.value) return;
  activeDialog.value = '';
  passwordDialogError.value = '';
  accountDialogError.value = '';
}

async function changePassword() {
  if (!token.value) return;

  if (passwordForm.value.newPassword.length < 6) {
    passwordDialogError.value = 'Yangi parol kamida 6 belgidan iborat bo‘lishi kerak.';
    return;
  }
  if (passwordForm.value.newPassword !== passwordForm.value.confirmPassword) {
    passwordDialogError.value = 'Yangi parollar bir xil emas.';
    return;
  }

  actionLoading.value = true;
  passwordDialogError.value = '';
  try {
    await axios.post(
      'users/change-password/',
      {
        old_password: passwordForm.value.oldPassword,
        new_password: passwordForm.value.newPassword,
        password2: passwordForm.value.confirmPassword,
      },
      {
        headers: { Authorization: `Bearer ${token.value}` },
      }
    );
    profileStatus.value = { type: 'success', text: 'Parol muvaffaqiyatli o‘zgartirildi.' };
    closeDialog();
  } catch (requestError) {
    passwordDialogError.value = getReadableError(requestError.response?.data, 'Parolni o‘zgartirishda xatolik yuz berdi.');
  } finally {
    actionLoading.value = false;
  }
}

async function changeAccountData() {
  if (!token.value) return;

  if (!accountForm.value.username || !accountForm.value.name) {
    accountDialogError.value = 'Barcha maydonlarni to‘ldiring.';
    return;
  }

  actionLoading.value = true;
  accountDialogError.value = '';
  try {
    await axios.post(
      'users/profile/',
      {
        username: accountForm.value.username,
        name: accountForm.value.name,
      },
      {
        headers: { Authorization: `Bearer ${token.value}` },
      }
    );
    await fetchProfileData();
    profileStatus.value = { type: 'success', text: 'Username va ism muvaffaqiyatli o‘zgartirildi.' };
    closeDialog();
  } catch (requestError) {
    accountDialogError.value = getReadableError(requestError.response?.data, 'Profil ma’lumotlarini o‘zgartirishda xatolik yuz berdi.');
  } finally {
    actionLoading.value = false;
  }
}

function requestDelete(phraseSlug) {
  pendingDeleteSlug.value = phraseSlug;
  profileStatus.value = { type: 'success', text: '' };
}

async function deletePhrase(phraseSlug) {
  actionLoading.value = true;
  try {
    await axios.delete(`phrases/phrases/${phraseSlug}/`, {
      headers: { Authorization: `Bearer ${token.value}` },
    });
    phrases.value = phrases.value.filter((phrase) => phrase.slug !== phraseSlug);
    pendingDeleteSlug.value = '';
    profileStatus.value = { type: 'success', text: 'So‘z muvaffaqiyatli o‘chirildi.' };
  } catch {
    profileStatus.value = { type: 'error', text: 'So‘zni o‘chirishda xatolik yuz berdi.' };
  } finally {
    actionLoading.value = false;
  }
}

function getReadableError(data, fallback) {
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  const firstValue = Object.values(data)[0];
  if (Array.isArray(firstValue)) return firstValue.join(' ');
  if (typeof firstValue === 'string') return firstValue;
  return fallback;
}

function getColorFromString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `#${((hash >> 3) & 0xFFFFFF).toString(16).padStart(6, '0')}`;
}

function formatDate(date) {
  return dayjs(date).format('DD/MM/YYYY');
}
</script>

<style scoped>
* {
  box-sizing: border-box;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.profile-page {
  --md-surface: var(--card-bg);
  --md-surface-variant: color-mix(in srgb, var(--text-color) 8%, var(--card-bg));
  --md-outline: color-mix(in srgb, var(--text-color) 16%, transparent);
  --md-muted: color-mix(in srgb, var(--text-color) 68%, transparent);
  --md-error: #b3261e;
  --md-success: #0f6b3e;
  --md-danger-container: color-mix(in srgb, var(--md-error) 10%, var(--card-bg));
  --md-radius: 12px;
  --md-elevation: 0 1px 2px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06);
  min-height: 100vh;
  padding: clamp(16px, 3vw, 32px);
  background: var(--bg-color);
  color: var(--text-color);
}

.page-shell {
  width: min(920px, 100%);
  margin-inline: auto;
  display: grid;
  gap: 16px;
}

.auth-shell {
  width: min(520px, 100%);
  min-height: calc(100vh - 64px);
  align-content: center;
}

.md-card {
  border: 1px solid var(--md-outline);
  border-radius: var(--md-radius);
  background: var(--md-surface);
  box-shadow: var(--md-elevation);
}

.auth-card,
.words-section,
.settings-section,
.loading-card {
  padding: clamp(20px, 4vw, 32px);
}

.section-header,
.section-row,
.profile-card,
.settings-section {
  display: flex;
  gap: 16px;
}

.section-header {
  flex-direction: column;
  margin-bottom: 24px;
}

.section-header.centered {
  align-items: center;
  text-align: center;
}

.section-header h1,
.profile-card h1,
.section-row h2,
.settings-section h2,
.dialog h2 {
  margin: 0;
  color: var(--text-color);
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.2;
}

.section-header h1,
.profile-card h1 {
  font-size: clamp(1.55rem, 3vw, 2.2rem);
}

.section-row h2,
.settings-section h2,
.dialog h2 {
  font-size: clamp(1.15rem, 2vw, 1.4rem);
}

.section-header p,
.muted {
  margin: 0;
  color: var(--md-muted);
}

.section-kicker {
  margin: 0 0 4px;
  color: var(--accent-color);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.md-form {
  display: grid;
  gap: 16px;
}

.field {
  display: grid;
  gap: 6px;
}

.field label {
  color: var(--md-muted);
  font-size: 0.875rem;
  font-weight: 700;
}

.md-input {
  width: 100%;
  min-height: 56px;
  padding: 14px 16px;
  border: 1px solid var(--md-outline);
  border-radius: 8px;
  background: var(--md-surface-variant);
  color: var(--text-color);
  font: inherit;
}

.md-input:focus {
  border-color: var(--accent-color);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent-color) 22%, transparent);
  outline: none;
}

.md-input.is-error {
  border-color: var(--md-error);
}

.supporting-text,
.toggle-text,
.status-message {
  margin: 0;
  font-size: 0.9rem;
}

.supporting-text.error,
.status-message.error {
  color: var(--md-error);
}

.status-message.success {
  color: var(--md-success);
}

.status-message {
  padding: 12px 14px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--text-color) 6%, transparent);
}

.status-message.error {
  background: var(--md-danger-container);
}

.toggle-text {
  color: var(--md-muted);
  text-align: center;
}

.md-button,
.text-button,
.icon-button {
  min-height: 44px;
  border: 0;
  border-radius: 999px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.md-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 20px;
}

.md-button.filled {
  background: var(--accent-color);
  color: #fff;
}

.md-button.filled.danger {
  background: var(--md-error);
}

.md-button.tonal,
.md-button.text {
  background: color-mix(in srgb, var(--accent-color) 12%, transparent);
  color: var(--accent-color);
}

.md-button.tonal.danger {
  background: var(--md-danger-container);
  color: var(--md-error);
}

.md-button:disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

.md-button:focus-visible,
.text-button:focus-visible,
.icon-button:focus-visible,
.word-card:focus-within {
  outline: 3px solid color-mix(in srgb, var(--accent-color) 34%, transparent);
  outline-offset: 2px;
}

.text-button {
  min-height: 36px;
  padding: 0 4px;
  background: transparent;
  color: var(--accent-color);
}

.button-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.profile-card {
  align-items: center;
  justify-content: space-between;
  padding: clamp(18px, 3vw, 24px);
}

.profile-identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 16px;
}

.avatar {
  display: grid;
  width: 72px;
  height: 72px;
  flex: 0 0 auto;
  place-items: center;
  overflow: hidden;
  border-radius: 50%;
  font-size: 1.6rem;
  font-weight: 700;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.loading-card {
  display: grid;
  gap: 16px;
}

.loading-card p {
  margin: 0;
  color: var(--md-muted);
}

.linear-progress {
  height: 4px;
  overflow: hidden;
  border-radius: 999px;
  background: color-mix(in srgb, var(--accent-color) 16%, transparent);
}

.linear-progress::before {
  display: block;
  width: 45%;
  height: 100%;
  border-radius: inherit;
  background: var(--accent-color);
  animation: progress 1.2s ease-in-out infinite;
  content: '';
}

.section-row {
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.count-chip {
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  border-radius: 999px;
  padding: 0 12px;
  background: var(--md-surface-variant);
  color: var(--md-muted);
  font-weight: 700;
}

.state-panel {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  padding: 18px;
  border: 1px solid var(--md-outline);
  border-radius: 8px;
  background: var(--md-surface-variant);
}

.state-panel i {
  color: var(--accent-color);
  font-size: 1.45rem;
}

.state-panel h3,
.state-panel p {
  margin: 0;
}

.state-panel h3 {
  font-size: 1rem;
}

.state-panel p {
  color: var(--md-muted);
}

.state-panel.error {
  background: var(--md-danger-container);
}

.state-panel.error i,
.state-panel.error h3 {
  color: var(--md-error);
}

.words-list {
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.word-card {
  padding: 18px;
  border: 1px solid var(--md-outline);
  border-radius: 8px;
  background: var(--md-surface);
}

.word-meta,
.word-heading,
.confirm-actions,
.dialog-header,
.dialog-actions,
.settings-actions {
  display: flex;
  align-items: center;
}

.word-meta {
  gap: 8px;
  color: var(--md-muted);
  font-size: 0.85rem;
}

.word-heading {
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
}

.word-heading h3 {
  margin: 0;
  color: var(--text-color);
  font-size: 1.15rem;
  line-height: 1.3;
  word-break: break-word;
}

.definition {
  margin: 10px 0 0;
  color: var(--text-color);
  line-height: 1.65;
  overflow-wrap: anywhere;
}

.example {
  margin: 14px 0 0;
  padding: 12px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--accent-color) 10%, transparent);
  color: var(--text-color);
}

.example span {
  color: var(--accent-color);
  font-weight: 700;
}

.icon-button {
  display: inline-grid;
  width: 44px;
  flex: 0 0 auto;
  place-items: center;
  background: transparent;
  color: var(--text-color);
}

.icon-button.danger {
  color: var(--md-error);
}

.confirm-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding: 12px;
  border-radius: 8px;
  background: var(--md-danger-container);
}

.confirm-panel p {
  margin: 0;
  color: var(--md-error);
  font-weight: 700;
}

.confirm-actions {
  gap: 8px;
}

.settings-section {
  align-items: center;
  justify-content: space-between;
}

.settings-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.dialog-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.42);
}

.dialog {
  width: min(520px, 100%);
  padding: 22px;
}

.dialog-header {
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.dialog-actions {
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
}

@keyframes progress {
  0% {
    transform: translateX(-110%);
  }
  100% {
    transform: translateX(230%);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 680px) {
  .profile-page {
    padding: 12px;
  }

  .profile-card,
  .section-row,
  .settings-section,
  .confirm-panel {
    align-items: stretch;
    flex-direction: column;
  }

  .profile-card .md-button,
  .settings-actions,
  .settings-actions .md-button,
  .confirm-actions,
  .confirm-actions .md-button {
    width: 100%;
  }

  .profile-identity {
    align-items: flex-start;
  }

  .state-panel {
    grid-template-columns: 1fr;
  }

  .dialog-actions {
    flex-direction: column-reverse;
  }

  .dialog-actions .md-button {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .button-spinner,
  .linear-progress::before {
    animation: none;
  }
}
</style>
